import { useEffect, useMemo, useRef, useState } from 'react';
import { MdLocationOff, MdRefresh } from 'react-icons/md';
import { RiMap2Line } from 'react-icons/ri';
import { Feature, Map as OlMap, View } from 'ol';
import { defaults as defaultControls } from 'ol/control';
import type { Coordinate } from 'ol/coordinate';
import { boundingExtent, getHeight, getWidth } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Point from 'ol/geom/Point';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat } from 'ol/proj';
import Cluster from 'ol/source/Cluster';
import XYZ from 'ol/source/XYZ';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';
import type { FeatureLike } from 'ol/Feature';
import { MonitoringQuery } from '../types';
import { getMonitoringLocationPoints } from '../../../services/monitoringAnalytics';
import baseMaps from '../../../screens/Map/baseMaps';
import { MapModal } from '../../../screens/Map/components/MapModal';
import * as Styled from './styles';
import maps from '../../../screens/Map/components/MapModal/maps';

type SessionLocationsMapProps = {
  filters: MonitoringQuery;
};

const BRAZIL_CENTER = fromLonLat([-51.9253, -14.235]);
const clusterStyles = new Map<number, Style>();

function getClusterStyle(feature: FeatureLike) {
  const features = feature.get('features') as Feature<Point>[] | undefined;
  const size = features?.length || 1;
  const cachedStyle = clusterStyles.get(size);

  if (cachedStyle) return cachedStyle;

  const radius = Math.min(26, 12 + Math.log2(size + 1) * 3);
  const fillColor = size >= 50 ? '#6f5bd3' : size >= 10 ? '#017ac6' : '#22a06b';
  const style = new Style({
    image: new CircleStyle({
      radius,
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
    text: new Text({
      text: String(size),
      fill: new Fill({ color: '#ffffff' }),
      font: '700 12px Inter, sans-serif',
    }),
  });

  clusterStyles.set(size, style);
  return style;
}

function getFeatureCoordinates(features: Feature<Point>[]) {
  return features.reduce<Coordinate[]>((coordinates, feature) => {
    const geometry = feature.getGeometry();
    if (geometry) coordinates.push(geometry.getCoordinates());
    return coordinates;
  }, []);
}

function focusCoordinates(map: OlMap, coordinates: Coordinate[], maxZoom: number) {
  if (!coordinates.length) return;

  const view = map.getView();
  const extent = boundingExtent(coordinates);

  if (coordinates.length === 1 || (getWidth(extent) === 0 && getHeight(extent) === 0)) {
    view.animate({
      center: coordinates[0],
      zoom: Math.min(maxZoom, Math.max((view.getZoom() || 4) + 2, 7)),
      duration: 350,
    });
    return;
  }

  view.fit(extent, {
    padding: [55, 55, 55, 55],
    maxZoom,
    duration: 350,
  });
}

function getErrorMessage(error: unknown) {
  const response = (error as { response?: { status?: number; data?: { detail?: string } } }).response;

  if (response?.status === 403) return 'Você não possui permissão para consultar as localizações.';
  if (response?.status === 400) return response.data?.detail || 'O período informado não é válido.';
  return 'Não foi possível carregar as localizações das sessões.';
}

export function SessionLocationsMap({ filters }: SessionLocationsMapProps) {
  const mapElementRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<OlMap | null>(null);
  const baseMapLayerRef = useRef<TileLayer<XYZ> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionCount, setSessionCount] = useState(0);
  const [baseMapIndex, setBaseMapIndex] = useState(0);
  const [isBaseMapModalOpen, setIsBaseMapModalOpen] = useState(false);
  const pointSource = useMemo(() => new VectorSource<Feature<Point>>({ wrapX: false }), []);
  const clusterSource = useMemo(
    () =>
      new Cluster<Feature<Point>>({
        distance: 40,
        minDistance: 12,
        source: pointSource,
        wrapX: false,
      }),
    [pointSource],
  );

  useEffect(() => {
    if (!mapElementRef.current) return undefined;

    const baseMapLayer = new TileLayer({ source: new XYZ(baseMaps[0]) });
    const map = new OlMap({
      target: mapElementRef.current,
      layers: [
        baseMapLayer,
        new VectorLayer({ source: clusterSource, style: getClusterStyle }),
      ],
      view: new View({
        center: BRAZIL_CENTER,
        zoom: 3.8,
        minZoom: 2,
        maxZoom: 17,
      }),
      controls: defaultControls({ rotate: false }),
    });

    const handleClick = map.on('singleclick', event => {
      const cluster = map.forEachFeatureAtPixel(event.pixel, feature => feature);
      const features = cluster?.get('features') as Feature<Point>[] | undefined;

      if (features?.length) focusCoordinates(map, getFeatureCoordinates(features), 12);
    });

    const handlePointerMove = map.on('pointermove', event => {
      const target = map.getTargetElement();
      target.style.cursor = map.hasFeatureAtPixel(event.pixel) ? 'pointer' : '';
    });

    mapRef.current = map;
    baseMapLayerRef.current = baseMapLayer;

    return () => {
      map.un('singleclick', handleClick.listener);
      map.un('pointermove', handlePointerMove.listener);
      map.setTarget(undefined);
      mapRef.current = null;
      baseMapLayerRef.current = null;
    };
  }, [clusterSource]);

  function handleChangeBaseMap(index: number) {
    const selectedBaseMap = baseMaps[index];
    if (!selectedBaseMap) return;

    baseMapLayerRef.current?.setSource(new XYZ(selectedBaseMap));
    setBaseMapIndex(index);
  }

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError('');
    setSessionCount(0);
    pointSource.clear();

    getMonitoringLocationPoints(
      {
        data_inicio: filters.data_inicio,
        data_fim: filters.data_fim,
      },
      controller.signal,
    )
      .then(response => {
        const features = new GeoJSON().readFeatures(response.data, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        }) as Feature<Point>[];

        pointSource.addFeatures(features);
        setSessionCount(features.length);
        setLoading(false);

        requestAnimationFrame(() => {
          const map = mapRef.current;
          if (!map || !features.length) return;

          map.updateSize();
          focusCoordinates(map, getFeatureCoordinates(features), 8);
        });
      })
      .catch(requestError => {
        if ((requestError as { code?: string }).code === 'ERR_CANCELED') return;

        setError(getErrorMessage(requestError));
        setLoading(false);
      });

    return () => controller.abort();
  }, [filters.data_fim, filters.data_inicio, pointSource]);

  const sessionLabel = sessionCount === 1 ? '1 sessão localizada' : `${sessionCount} sessões localizadas`;

  return (
    <Styled.MapCard>
      <Styled.MapHeader>
        <div>
          <h3>Mapa das localizações das sessões</h3>
          <p>Agrupamento das sessões que forneceram coordenadas no período selecionado.</p>
        </div>
        {!loading && !error && <Styled.SessionCount>{sessionLabel}</Styled.SessionCount>}
      </Styled.MapHeader>
      <Styled.MapFrame>
        <Styled.MapCanvas
          ref={mapElementRef}
          role="region"
          aria-label="Mapa com agrupamentos das localizações das sessões"
        />
        <Styled.BaseMapButton
          type="button"
          onClick={() => setIsBaseMapModalOpen(true)}
          aria-label="Alterar mapa base"
          title="Alterar mapa base"
        >
          <RiMap2Line aria-hidden="true" />
          <span>Mapa base</span>
        </Styled.BaseMapButton>
        {loading && (
          <Styled.MapFeedback aria-live="polite">
            <MdRefresh aria-hidden="true" />
            <strong>Carregando localizações...</strong>
          </Styled.MapFeedback>
        )}
        {!loading && error && (
          <Styled.MapFeedback $error role="alert">
            <MdLocationOff aria-hidden="true" />
            <strong>{error}</strong>
            <span>Altere os filtros ou tente atualizar a seção novamente.</span>
          </Styled.MapFeedback>
        )}
        {!loading && !error && sessionCount === 0 && (
          <Styled.MapFeedback>
            <MdLocationOff aria-hidden="true" />
            <strong>Sem localizações no período</strong>
            <span>Nenhuma sessão forneceu coordenadas para o intervalo selecionado.</span>
          </Styled.MapFeedback>
        )}
         <Styled.MapDisclaimer>
          <span>
            Mapa:{' '}
            <a
              className="notranslate"
              href={maps[baseMapIndex].href}
              target="_blank"
              rel="noreferrer"
            >
              {maps[baseMapIndex].organization}
            </a>
          </span>
        </Styled.MapDisclaimer>
      </Styled.MapFrame>
      <Styled.MapHint>
        Cada número representa a quantidade de sessões agrupadas. Clique em um agrupamento para aproximar o mapa.
      </Styled.MapHint>
      {isBaseMapModalOpen && (
        <MapModal
          setIsOpen={setIsBaseMapModalOpen}
          onChangeBaseMap={handleChangeBaseMap}
        />
      )}
    </Styled.MapCard>
  );
}
